/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy
} from "firebase/firestore";

const state = {
  companyTags: [],
  loadingCompanyTags: false
};

const actions = {
  async loadCompanyTags({ commit, getters }) {
    commit("setLoadingCompanyTags", true);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const companyTagsRef = collection(programRef, "companyTags");
      const companyTagsQuery = query(companyTagsRef, orderBy("tagUppercase"));
      const querySnapshot = await getDocs(companyTagsQuery);

      const companyTags = [];
      querySnapshot.forEach(function(docSnapshot) {
        const data = docSnapshot.data();
        companyTags.push({
          id: docSnapshot.id,
          tag: data.tag,
          tagUppercase: data.tagUppercase,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setCompanyTags", companyTags);
    } catch (error) {
      console.error("Error loading company tags:", error);
      commit("setCompanyTags", []);
    } finally {
      commit("setLoadingCompanyTags", false);
    }
  },

  async getCompanyTagById({ getters }, tagId) {
    try {
      const programRef = doc(db(), "programs", getters.programId);
      const companyTagsRef = collection(programRef, "companyTags");
      const tagRef = doc(companyTagsRef, tagId);
      const tagDoc = await getDoc(tagRef);

      if (tagDoc.exists()) {
        const data = tagDoc.data();
        return {
          id: tagDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Company tag with ID ${tagId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting company tag by ID:", error);
      return null;
    }
  },

  async createCompanyTag({ commit, getters, dispatch }, payload) {
    try {
      const programRef = doc(db(), "programs", getters.programId);
      const companyTagsRef = collection(programRef, "companyTags");

      // Check for duplicate tags
      const duplicateQuery = query(
        companyTagsRef,
        where("tagUppercase", "==", payload.tag.toUpperCase())
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        throw new Error("Company tag already exists");
      }

      const tagData = {
        tag: payload.tag,
        tagUppercase: payload.tag.toUpperCase(),
        created: new Date(),
        updated: new Date()
      };

      const newTagRef = await addDoc(companyTagsRef, tagData);

      // Reload tags to get updated list
      await dispatch("loadCompanyTags");

      return { id: newTagRef.id, ...tagData };
    } catch (error) {
      console.error("Error creating company tag:", error);
      throw error.message || "Error occurred when creating company tag";
    }
  },

  async updateCompanyTag({ commit, getters, dispatch }, { id, tag }) {
    try {
      const programRef = doc(db(), "programs", getters.programId);
      const companyTagsRef = collection(programRef, "companyTags");
      const tagRef = doc(companyTagsRef, id);

      // Check for duplicate tags (excluding current tag)
      const duplicateQuery = query(
        companyTagsRef,
        where("tagUppercase", "==", tag.toUpperCase())
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);

      let isDuplicate = false;
      duplicateSnapshot.forEach(docSnapshot => {
        if (docSnapshot.id !== id) {
          isDuplicate = true;
        }
      });

      if (isDuplicate) {
        throw new Error("Company tag already exists");
      }

      const updateData = {
        tag: tag,
        tagUppercase: tag.toUpperCase(),
        updated: new Date()
      };

      await updateDoc(tagRef, updateData);

      // Reload tags to get updated list
      await dispatch("loadCompanyTags");

      return { id, ...updateData };
    } catch (error) {
      console.error("Error updating company tag:", error);
      throw error.message || "Error occurred when updating company tag";
    }
  },

  async deleteCompanyTag({ commit, getters, dispatch }, tagId) {
    try {
      const programRef = doc(db(), "programs", getters.programId);
      const companyTagsRef = collection(programRef, "companyTags");
      const tagRef = doc(companyTagsRef, tagId);

      await deleteDoc(tagRef);

      // Reload tags to get updated list
      await dispatch("loadCompanyTags");

      return true;
    } catch (error) {
      console.error("Error deleting company tag:", error);
      throw "Error occurred when deleting company tag";
    }
  }
};

const mutations = {
  setCompanyTags(state, payload) {
    state.companyTags = payload;
  },

  setLoadingCompanyTags(state, payload) {
    state.loadingCompanyTags = payload;
  }
};

const getters = {
  companyTags(state) {
    return state.companyTags;
  },

  loadingCompanyTags(state) {
    return state.loadingCompanyTags;
  },

  getCompanyTagByName: state => tagName => {
    return state.companyTags.find(
      tag => tag.tag.toLowerCase() === tagName.toLowerCase()
    );
  },

  companyTagsCount(state) {
    return state.companyTags.length;
  },

  companyTagsArray(state) {
    return state.companyTags.map(tag => tag.tag);
  },

  companyTagsOptions(state) {
    return state.companyTags.map(tag => ({
      text: tag.tag,
      value: tag.id
    }));
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
